import { getFirstSelection } from '../../selectionUtils/tableSelectionAccessors';
import { isSelectingSingleSelection } from '../../selectionUtils/tableSelectionPredicateUtils';
import type { MNHotInstance } from '../../../tableHotInstanceTypes';
import { isFormula } from '../../dataUtils/formula/tableFormulaPredicateUtils';
import { FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/FormulaAutocompleteHotPlugin';
import type FormulaAutocompleteHotPlugin from '../../../plugins/FormulaAutocompleteHotPlugin';
import type DraftJSFormulaAutocompleteHotPlugin from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import { DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME } from '../../../plugins/DraftJSFormulaAutocompleteHotPlugin';
import type { CellSelection } from '../../../../../../common/table/TableTypes';

const FORMULA_TRIGGER = '=';

type TableFormulaEditor = NonNullable<ReturnType<MNHotInstance['getMNEditor']>>;

const isEnabledFormulaAutocompletePlugin = (
    plugin: DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined,
): boolean => !!plugin && (!plugin.isEnabled || plugin.isEnabled());

// TODO: This can be removed and inlined once DraftJSFormulaAutocompleteHotPlugin is removed
const getFormulaAutocompletePlugin = (
    hotInstance: MNHotInstance,
): DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined => {
    const plugins = [
        hotInstance.getPlugin(FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
        hotInstance.getPlugin(DRAFTJS_FORMULA_AUTOCOMPLETE_PLUGIN_NAME),
    ] as unknown as Array<DraftJSFormulaAutocompleteHotPlugin | FormulaAutocompleteHotPlugin | undefined>;

    return plugins.find(isEnabledFormulaAutocompletePlugin);
};

/**
 * Starts formula editing from a selected cell and returns the text the dropdown should query against.
 */
const beginFormulaEditing = (
    hotInstance: MNHotInstance,
    activeEditor: TableFormulaEditor,
    cellSelection: CellSelection,
): string => {
    hotInstance.selectCell(...cellSelection, false, true);

    const [row, col] = cellSelection;
    const cellText = hotInstance.getSourceDataAtCell(row, col)?.toString() || '';

    // If cell is already a formula, preserve the content of the cell and start editing
    if (isFormula(cellText)) {
        activeEditor.beginEditing();
        return cellText;
    }
    // If cell is not a formula, begin editing cell with `=`
    activeEditor.beginEditing(FORMULA_TRIGGER);
    return FORMULA_TRIGGER;
};

/**
 * Prepares an already open editor for formula autocomplete and returns the text the dropdown should query against.
 */
const continueEditingForFormula = (activeEditor: TableFormulaEditor): string => {
    const editorText = activeEditor.getPlainTextContent();
    if (isFormula(editorText)) return editorText;

    // If cell is not a formula, replace cell content with `=`
    activeEditor.setPlainTextContent(FORMULA_TRIGGER);
    return FORMULA_TRIGGER;
};

/**
 * Adds a formula to the currently selected cell in the Handsontable instance.
 */
export const addHotTableFormulaToCell = (hotInstance: MNHotInstance, cellSelections = hotInstance.getSelected()) => {
    if (!cellSelections) return;

    if (!isSelectingSingleSelection(cellSelections)) return;

    const cellSelection = getFirstSelection(cellSelections);
    if (!cellSelection) return;

    requestAnimationFrame(() => {
        if (hotInstance.isDestroyed) return;

        const activeEditor = hotInstance.getMNEditor();
        if (!activeEditor) return;

        const formulaAutocompletePlugin = getFormulaAutocompletePlugin(hotInstance);

        if (!activeEditor.isOpened()) {
            const formulaText = beginFormulaEditing(hotInstance, activeEditor, cellSelection);
            formulaAutocompletePlugin?.openFormulaDropdown(formulaText);
            return;
        }

        const formulaText = continueEditingForFormula(activeEditor);
        formulaAutocompletePlugin?.openFormulaDropdown(formulaText);
        activeEditor.focus();
    });
};
